import { Command } from 'commander';
import { ApiClient } from '../client.js';
import { ApiError, errorDetails } from '../errors.js';
import { formatOutput } from '../output.js';
import { getBase, getFormat, mcpToolsetUrl } from '../helpers.js';
import { requireActiveOrg } from '../require-active-org.js';
import { parseDuration } from '../duration.js';
import { confirm } from '../prompt.js';
import { isInteractive } from '../env.js';
import { collectPages } from '../paginate.js';

export const tokensCommand = new Command('tokens')
  .description('Manage API tokens');

interface TokenLifecycleOpts {
  expiresAt?: string;
  ttl?: string;
  description?: string;
}

/** `--ttl`/`--expires-at`/`--description` — shared by `create` and `create-for-toolset`. */
function addTokenLifecycleOptions(cmd: Command): Command {
  return cmd
    .option('--expires-at <date>', 'Expiration date (ISO 8601, e.g., 2026-06-01T00:00:00Z)')
    .option('--ttl <duration>', 'Relative expiry, e.g. 10m, 2h, 7d (use for disposable probe/test tokens)')
    .option('--description <text>', 'What holds this token (e.g. "behind wayai credential X"); shown in list and revoke warnings');
}

/** `--ttl` is sugar for a relative `expires_at`; the server only ever sees the ISO date. */
function resolveExpiresAt(opts: TokenLifecycleOpts): string | undefined {
  if (opts.ttl && opts.expiresAt) {
    throw new Error('--ttl and --expires-at are mutually exclusive');
  }
  if (opts.ttl) return new Date(Date.now() + parseDuration(opts.ttl)).toISOString();
  return opts.expiresAt;
}

/** Fold the resolved expiry + description onto a create request body. */
function applyTokenLifecycleOpts(body: Record<string, unknown>, opts: TokenLifecycleOpts): void {
  const expiresAt = resolveExpiresAt(opts);
  if (expiresAt) body.expires_at = expiresAt;
  if (opts.description) body.description = opts.description;
}

addTokenLifecycleOptions(
  tokensCommand.command('create')
    .description('Create a scoped API token')
    .requiredOption('--name <name>', 'Token name')
    .requiredOption('--grants <json>', 'Grant definitions as JSON array'),
)
  .action(async function (this: Command, opts: TokenLifecycleOpts & { name: string; grants: string }) {
    await requireActiveOrg();
    const client = new ApiClient();
    let grants: unknown;
    try {
      grants = JSON.parse(opts.grants);
    } catch {
      throw new Error('--grants must be valid JSON');
    }
    const body: Record<string, unknown> = { name: opts.name, grants };
    applyTokenLifecycleOpts(body, opts);
    const data = await client.request('POST', '/v1/tokens', body);
    console.log(formatOutput(data, getFormat(this)));
  });

addTokenLifecycleOptions(
  tokensCommand.command('create-for-toolset <slug>')
    .description('Create a toolset-bound (consumer) token whose authorization IS the toolset tool surface — it can call exactly the tools that toolset exposes (those record_types + operations, relationships, batch, and sql only if enabled) and nothing else. Binds to the configured --base.')
    .option('--name <name>', 'Token name (default: "<slug> consumer")'),
)
  .action(async function (this: Command, slug: string, opts: TokenLifecycleOpts & { name?: string }) {
    await requireActiveOrg();
    const database = getBase(this);
    const client = new ApiClient();
    const body: Record<string, unknown> = {
      name: opts.name ?? `${slug} consumer`,
      toolset_binding: { base_id: database, toolset_id: slug },
    };
    applyTokenLifecycleOpts(body, opts);
    const data = await client.request('POST', '/v1/tokens', body);
    const format = getFormat(this);
    console.log(formatOutput(data, format));
    // "create the server, here's its token and URL"
    if (format !== 'json') {
      console.log(`\nMCP URL: ${mcpToolsetUrl(slug)}`);
    }
  });

tokensCommand.command('list')
  .description('List API tokens (status, last_used_at, expires_at, description)')
  .action(async function (this: Command) {
    const client = new ApiClient();
    const tokens = await listAllTokens(client);
    console.log(formatOutput(tokens, getFormat(this)));
  });

tokensCommand.command('revoke <token_id>')
  .description('Revoke an API token (permanent). A recently-used or toolset-bound token requires --force.')
  .option('--force', 'Revoke even if the token appears to be in live use')
  .action(async function (this: Command, tokenId: string, opts: { force?: boolean }) {
    const client = new ApiClient();
    const path = (force: boolean) => `/v1/tokens/${tokenId}${force ? '?force=true' : ''}`;
    if (opts.force) {
      await client.request('DELETE', path(true));
      console.log('Token revoked');
      return;
    }
    try {
      await client.request('DELETE', path(false));
      console.log('Token revoked');
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 409 || errorDetails(err)?.requires_force !== true) {
        throw err;
      }
      // Guardrail (#650): the token looks live (recently used and/or toolset-bound).
      // Require an explicit decision — never auto-confirm in a pipe: agents run
      // non-interactive, and an accidental batch revoke of live consumer tokens is
      // exactly the incident this guard exists to stop.
      console.error(err.message);
      if (!isInteractive()) {
        console.error('\nRe-run with --force to revoke anyway.');
        process.exit(1);
      }
      const ok = await confirm('Revoke anyway?', { defaultYes: false });
      if (!ok) {
        console.error('Aborted.');
        process.exit(1);
      }
      await client.request('DELETE', path(true));
      console.log('Token revoked');
    }
  });

interface TokenListItem {
  token_id: string;
  name: string;
  created_at: string;
  status?: 'active' | 'expired';
  usage_tracked?: boolean;
  last_used_at?: string;
  expires_at?: string;
  description?: string;
  toolset_binding?: { base_id: string; toolset_id: string };
}

// GET /v1/tokens is a bounded page + meta.cursor (#669) — follow it so `list`/`prune` still
// see the whole fleet. Use the max page size to minimize round-trips. The server bounds its
// own KV reads per page, so a large fleet costs several sequential requests, never a 500.
const TOKEN_LIST_PAGE_LIMIT = 250;

function listAllTokens(client: ApiClient): Promise<TokenListItem[]> {
  return collectPages<TokenListItem>(
    client,
    (cursor) => `/v1/tokens?limit=${TOKEN_LIST_PAGE_LIMIT}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
  );
}

interface PruneRow {
  token_id: string;
  name: string;
  reason: string;
  last_used: string;
  [key: string]: string;
}

function pruneRow(t: TokenListItem, reason: string): PruneRow {
  return {
    token_id: t.token_id,
    name: t.name,
    reason,
    last_used: t.last_used_at ?? 'never',
  };
}

tokensCommand.command('prune')
  .description('Revoke stale tokens, selected by staleness only (expired, or unused past --unused-since) — never by name. Previews by default; revoking requires --yes or an interactive confirmation.')
  .option('--unused-since <duration>', 'Also prune tokens with no activity for this long (e.g. 30d). Skips toolset-bound and legacy usage-untracked tokens.')
  .option('--include-bound', 'Allow pruning stale toolset-bound (consumer) tokens')
  .option('-y, --yes', 'Revoke without prompting (required when non-interactive)')
  .action(async function (this: Command, opts: { unusedSince?: string; includeBound?: boolean; yes?: boolean }) {
    const staleMs = opts.unusedSince ? parseDuration(opts.unusedSince) : undefined;
    const client = new ApiClient();
    const tokens = await listAllTokens(client);
    const now = Date.now();

    const targets: PruneRow[] = [];
    const skipped: PruneRow[] = [];
    for (const t of tokens) {
      const expired = t.status ? t.status === 'expired' : !!t.expires_at && Date.parse(t.expires_at) <= now;
      if (expired) {
        // Expiry is authoritative — an expired token is dead regardless of binding or usage tracking.
        targets.push(pruneRow(t, 'expired'));
        continue;
      }
      if (staleMs === undefined) continue;
      const lastActivity = Date.parse(t.last_used_at ?? t.created_at);
      if (!(now - lastActivity > staleMs)) continue;
      if (t.usage_tracked === false) {
        // Legacy tokens never record last_used_at, so "stale" is unknowable — a live one
        // would look never-used. Never prunable via staleness (the incident-class trap).
        skipped.push(pruneRow(t, 'usage untracked (legacy token) — revoke individually if truly unused'));
      } else if (t.toolset_binding && !opts.includeBound) {
        skipped.push(pruneRow(t, `toolset-bound ('${t.toolset_binding.toolset_id}') — pass --include-bound to prune`));
      } else {
        targets.push(pruneRow(t, `unused for over ${opts.unusedSince}`));
      }
    }

    const format = getFormat(this);
    const printSkipped = () => {
      if (skipped.length > 0) {
        console.log(`\nSkipped ${skipped.length} stale token(s):`);
        console.log(formatOutput(skipped, format));
      }
    };

    if (targets.length === 0) {
      console.log('Nothing to prune.');
      printSkipped();
      return;
    }

    console.log(`Would revoke ${targets.length} token(s):`);
    console.log(formatOutput(targets, format));
    printSkipped();

    if (!opts.yes) {
      if (!isInteractive()) {
        console.error('\nPreview only — re-run with --yes to revoke these tokens.');
        process.exit(1);
      }
      const ok = await confirm(`\nRevoke ${targets.length} token(s)?`, { defaultYes: false });
      if (!ok) {
        console.error('Aborted.');
        process.exit(1);
      }
    }

    let revoked = 0;
    let failed = 0;
    for (const t of targets) {
      try {
        // force=true: prune already confirmed above, and expired targets are dead anyway.
        await client.request('DELETE', `/v1/tokens/${t.token_id}?force=true`);
        revoked++;
      } catch (err) {
        failed++;
        console.error(`Failed to revoke ${t.token_id} ('${t.name}'): ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    console.log(`Revoked ${revoked} token(s)${failed > 0 ? `, ${failed} failed` : ''}.`);
    if (failed > 0) process.exit(1);
  });
